import { pluralCategory } from '@/i18n/format.ts'
import type { Messages } from '@/i18n/messages/en/index.ts'

/**
 * `chat` in Brazilian Portuguese. Declared, never inferred, so a gap fails `tsc`.
 *
 * Register is `você`, informal-professional (LOC-061). The ratified vocabulary of ADR-0018
 * governs every noun here: `conversa` and never *bate-papo* or *chat*, `resposta`,
 * `pergunta`, `fonte` for a citation and never for a base, `trecho` and never *pedaço*,
 * `etapa` and never *passo*, `execução` and never *rodada*.
 *
 * ── The register line this file must not cross ──
 *
 * This is the conversation column. It says what an answer is MADE OF, in a reader's words;
 * `trace` says what the machine DID, in the machine's. So `phaseRetrieval` is "consultando"
 * and never *recuperação*, `evidenceScale` is "palavras ◂ ▸ sentido" and never
 * *léxico* nem *semântico*, and `totalTokens` says "quanto leu e escreveu" even though `token`
 * is a term of art the glossary would otherwise allow (D-15 permits it; the English chose
 * not to say it, and Portuguese holds the same line — AGM-022).
 *
 * ── Gender ──
 *
 * `execução` is feminine, so every status in `runStatus` is a feminine participle:
 * "concluída", "cancelada", "suspensa". `etapa` is feminine too, which is why `stepFailed`
 * is the gender-free verb "falhou" rather than an adjective that would have to agree.
 * `fonte` is feminine, so `sourceUnverified` and `sourceStale` agree with it.
 */
export const chat: Messages['chat'] = {
  answeredBy: (agentName) => `${agentName} respondeu`,
  cited: 'de documentos',
  citationTitle: (documentTitle, locator, supported) =>
    supported ? `${documentTitle} — ${locator}` : `${documentTitle} — ${locator} (apoio não verificado)`,

  sources: 'Fontes',
  sourceUnverified: 'não verificada',
  sourceStale: 'desatualizada',
  sourceOpen: (documentTitle, locator) => `${documentTitle} — ${locator}`,

  drawerTitle: 'Detalhe da fonte',
  drawerClose: 'Fechar',
  drawerCloseBackdrop: 'Fechar o detalhe da fonte',
  /** "Trecho encontrado", não *recuperado*: a coluna da conversa não fala em recuperação. */
  drawerPassage: 'Trecho encontrado',
  drawerMeta: (sourceName, locator, score) => `${sourceName} · ${locator} · relevância ${score}`,
  drawerUnsupported:
    'Este trecho foi encontrado, mas não foi possível conferir se a redação da afirmação que ele sustenta corresponde ao que está escrito aqui. Leia o trecho antes de confiar na frase a que ele está ligado.',
  /**
   * Dois-pontos, e nenhuma preposição. `formatAgo` já devolve "há 5 min." com a preposição
   * dentro, e devolve "20 de set." sem nenhuma quando passa de uma semana — uma frase que
   * acrescentasse "em" renderizaria "indexado em há 5 min." num dos dois casos (LOC-022).
   */
  drawerIndexed: (when, stale) =>
    stale ? `indexado: ${when} · além do prazo de atualização desta fonte` : `indexado: ${when}`,

  provenanceTitle: 'Como isto foi feito',
  totalTime: 'tempo gasto',
  totalTokens: 'quanto leu e escreveu',
  totalCost: 'custo',

  phaseModel: 'pensando',
  phaseWriting: 'escrevendo',
  phaseRetrieval: 'consultando',
  phaseTool: 'fazendo algo',
  phaseApproval: 'aguardando você',

  runStatus: {
    queued: 'na fila',
    running: 'em andamento',
    waiting_for_approval: 'aguardando aprovação',
    waiting_for_input: 'aguardando entrada',
    waiting_for_external: 'aguardando sistema externo',
    suspended: 'suspensa',
    completed: 'concluída',
    partially_completed: 'parcialmente concluída',
    failed: 'falhou',
    cancelled: 'cancelada',
    expired: 'expirada'
  },

  tapeSummary: (spans) => `Para onde foi o tempo: ${spans}.`,
  tapeSpan: (phase, duration) => `${phase} ${duration}`,
  tapeSpanTitle: (phase, duration) => `${phase} · ${duration}`,
  tapeHint: 'largura = tempo gasto',

  showWork: 'Ver como foi feito',
  hideWork: 'Ocultar como foi feito',

  evidenceTitle: 'Evidências',
  evidenceScale: 'palavras ◂ ▸ sentido',
  evidenceUncited: '—',
  evidenceRank: (rank) => (rank == null ? '—' : `#${rank}`),

  stepsTitle: 'Etapas',
  /**
   * O que uma etapa FEZ, para quem lê a conversa. Os mesmos seis códigos são redigidos de
   * novo em `trace.stepLabel`, e os dois não podem convergir: lá se nomeia o perfil de
   * modelo, aqui não se nomeia nada disso (AGM-022). Gerúndios, porque os dois lugares que
   * mostram isto descrevem trabalho em curso ou recém-feito.
   *
   * `preferência` e `resposta` são femininas; `comunicado` e `agente` são masculinos.
   */
  stepActivity: {
    model_call: () => 'pensando',
    knowledge_search: () => 'consultando',
    memory_write: () => 'guardando uma preferência',
    announcement: () => 'enviando um comunicado',
    delegation: () => 'pedindo a outro agente',
    /** Nome vindo do servidor que fornece a ferramenta: dado, exibido tal e qual (D-17). */
    tool_call: ({ tool }) => tool
  },

  /** Zero fica no singular em português — "0 trecho usado" —, que é o motivo de `pluralCategory`. */
  stepPassages: (fmt, kept) =>
    pluralCategory(fmt.locale, kept) === 'one' ? `${kept} trecho usado` : `${kept} trechos usados`,
  stepFailed: 'falhou',
  noActions: 'Nenhuma ação foi tomada.',

  inspect: 'mostrar rastro',
  inspecting: 'mostrando rastro',

  stopped: 'Você interrompeu esta resposta.',

  noConversationTitle: 'Nenhuma conversa selecionada',
  noConversationBody: 'Escolha uma conversa à esquerda, ou comece uma nova.',
  /** Entra em "Olá, ___." — uma palavra de saudação, não um nome, por isso traduz. */
  greetingFallbackName: 'você',

  thinking: 'pensando…',
  starting: 'iniciando…',
  runFailed: 'A execução não terminou.',

  official: 'oficial',
  aboutShort: 'sobre',

  composerLabel: 'Mensagem',
  composerSendHint: 'para enviar',
  sendMessage: 'Enviar mensagem',
  stopGenerating: 'Interromper a resposta',

  showTrace: 'Mostrar rastro',
  hideTrace: 'Ocultar rastro'
}
